import { useCallback, useEffect, useState, type FormEvent } from "react";
import { tEnglish as t, type PhraseKey } from "@buza/i18n";
import { api } from "../api.js";
import { useSelectedTenant } from "../shell/tenant.js";
import { FieldReason, ifMatch, RefusalNotice, toRefusal, type Refusal } from "../zd/refusal.js";

// Z06 Operations inbox (S-062; spec 4.3 Z06, ENT-06, NFR-15). Five tabs; each item names its
// tenant; assign and close follow the ZD error pattern (zd/refusal.tsx, BD-63). The server
// decides who may do what: the screen offers only the actions the api lists for each item.
// Tabs whose records arrive with later stories say what will appear there (BD-62).

export type OpsTab = "failed" | "overdue" | "privacy" | "support" | "remembrance";
type Awaited = "notifications" | "overdue_queues" | "privacy_requests" | "support_log" | "remembrance_queues";

export interface OpsItem {
  sourceType: string;
  sourceId: string;
  tab: OpsTab;
  kind: string;
  tenant: { id: string; name: string };
  funeralId: string | null;
  occurredAt: string;
  detail: Record<string, unknown>;
  status: "open" | "closed";
  version: number;
  assignee: { id: string; name: string } | null;
  assignedAt: string | null;
  assignedBy: { id: string; name: string } | null;
  closedAt: string | null;
  closedBy: { id: string; name: string } | null;
  closeNote: string | null;
  actions: Array<"assign" | "close">;
}

export interface InboxAnswer {
  tenant: { id: string; name: string; status: string } | null;
  tab: OpsTab;
  status: "open" | "closed";
  tabs: Array<{ id: OpsTab; open: number; sources: string[]; awaiting: Awaited[] }>;
  items: OpsItem[];
  limit: number;
}

const TABS: ReadonlyArray<{ id: OpsTab; label: PhraseKey }> = [
  { id: "failed", label: "console.ops.tab_failed" },
  { id: "overdue", label: "console.ops.tab_overdue" },
  { id: "privacy", label: "console.ops.tab_privacy" },
  { id: "support", label: "console.ops.tab_support" },
  { id: "remembrance", label: "console.ops.tab_remembrance" },
];

const AWAITING: Record<Awaited, PhraseKey> = {
  notifications: "console.ops.awaiting_notifications",
  overdue_queues: "console.ops.awaiting_overdue_queues",
  privacy_requests: "console.ops.awaiting_privacy_requests",
  support_log: "console.ops.awaiting_support_log",
  remembrance_queues: "console.ops.awaiting_remembrance_queues",
};

const KIND: Record<string, PhraseKey> = {
  pdf_failed: "console.ops.kind_pdf_failed",
};

const DETAIL: Record<string, PhraseKey> = {
  template: "console.ops.detail_template",
  errorCode: "console.ops.detail_error",
  attempts: "console.ops.detail_attempts",
};

const dateTime = new Intl.DateTimeFormat("en-ZA", { dateStyle: "medium", timeStyle: "short", timeZone: "Africa/Johannesburg" });
const itemPath = (i: OpsItem, action: string) => `/v1/zd/ops/items/${i.sourceType}/${i.sourceId}/${action}`;

interface Assignee {
  id: string;
  name: string;
}

/** One item's assign and close controls. Its entries survive a refusal (spec 4.3). */
function ItemActions({
  item,
  me,
  assignees,
  onChanged,
  onReload,
}: {
  item: OpsItem;
  me: string;
  assignees: Assignee[];
  onChanged: (next: OpsItem, message: PhraseKey) => void;
  onReload: () => void;
}) {
  const [assignTo, setAssignTo] = useState(item.assignee?.id ?? "");
  const [closing, setClosing] = useState(false);
  const [note, setNote] = useState("");
  const [refusal, setRefusal] = useState<Refusal | undefined>();
  const [busy, setBusy] = useState(false);

  async function send(action: "assign" | "close", body: unknown, done: PhraseKey) {
    setBusy(true);
    const r = await api<{ item: OpsItem }>(itemPath(item, action), { method: "POST", body, headers: ifMatch(item.version) });
    setBusy(false);
    const refused = toRefusal(r);
    setRefusal(refused);
    if (r.ok) {
      setClosing(false);
      setNote("");
      onChanged(r.data.item, done);
    }
  }

  function close(e: FormEvent) {
    e.preventDefault();
    void send("close", { note }, "console.ops.saved_close");
  }

  if (item.actions.length === 0) return <RefusalNotice refusal={refusal} onReload={onReload} />;
  const fieldId = `note-${item.sourceId}`;
  return (
    <div className="ops-actions">
      <RefusalNotice refusal={refusal} onReload={onReload} />
      {item.actions.includes("assign") && (
        <div className="ops-assign">
          {item.assignee?.id !== me && (
            <button type="button" disabled={busy} onClick={() => send("assign", { assigneeId: me }, "console.ops.saved_assign")}>
              {t("console.ops.assign_me")}
            </button>
          )}
          <label>
            {t("console.ops.assign_label")}
            <select value={assignTo} onChange={(e) => setAssignTo(e.target.value)} aria-invalid={!!refusal?.fields.assigneeId}>
              <option value="">{t("console.ops.unassigned")}</option>
              {assignees.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            disabled={busy}
            onClick={() =>
              send("assign", { assigneeId: assignTo || null }, assignTo ? "console.ops.saved_assign" : "console.ops.saved_unassign")
            }
          >
            {t("console.ops.assign_save")}
          </button>
          <FieldReason refusal={refusal} field="assigneeId" />
        </div>
      )}
      {item.actions.includes("close") &&
        (closing ? (
          <form onSubmit={close} noValidate className="ops-close" aria-label={t("console.ops.close")}>
            <label htmlFor={fieldId}>{t("console.ops.close_note_label")}</label>
            <textarea
              id={fieldId}
              name="note"
              maxLength={1000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              aria-invalid={!!refusal?.fields.note}
              aria-describedby={`${fieldId}-hint`}
            />
            <p className="hint" id={`${fieldId}-hint`}>
              {t("console.ops.close_note_hint")}
            </p>
            <FieldReason refusal={refusal} field="note" />
            <button type="submit" disabled={busy}>
              {busy ? t("console.common.busy") : t("console.ops.close_confirm")}
            </button>
            <button type="button" disabled={busy} onClick={() => setClosing(false)}>
              {t("console.ops.cancel")}
            </button>
          </form>
        ) : (
          <button type="button" onClick={() => setClosing(true)}>
            {t("console.ops.close")}
          </button>
        ))}
    </div>
  );
}

export function OpsInbox({ me }: { me: string }) {
  const { selected } = useSelectedTenant();
  const tenantId = selected?.id ?? null;
  const [tab, setTab] = useState<OpsTab>("failed");
  const [status, setStatus] = useState<"open" | "closed">("open");
  const [answer, setAnswer] = useState<InboxAnswer | undefined>();
  const [loadError, setLoadError] = useState<string | undefined>();
  const [assignees, setAssignees] = useState<Assignee[]>([]);
  const [notice, setNotice] = useState<PhraseKey | undefined>();
  // Bumped on Reload so each item's controls start again from the fresh record.
  const [generation, setGeneration] = useState(0);

  const load = useCallback(async () => {
    const q = new URLSearchParams({ tab, status });
    if (tenantId) q.set("tenant", tenantId);
    const r = await api<InboxAnswer>(`/v1/zd/ops/inbox?${q.toString()}`);
    if (r.ok) {
      setAnswer(r.data);
      setLoadError(undefined);
    } else {
      setLoadError(r.status === 0 ? t("common.errors.no_connection") : (r.error?.message ?? t("console.ops.load_failed")));
    }
  }, [tab, status, tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void api<{ assignees: Assignee[] }>("/v1/zd/ops/assignees").then((r) => r.ok && setAssignees(r.data.assignees));
  }, []);

  const reload = () => {
    setNotice(undefined);
    setGeneration((g) => g + 1);
    void load();
  };

  const counts = new Map(answer?.tabs.map((x) => [x.id, x]) ?? []);
  const current = counts.get(tab);
  const items = answer?.tab === tab ? answer.items : [];

  return (
    <section className="z06" aria-labelledby="z06-title">
      <h2 id="z06-title">{t("console.ops.title")}</h2>
      <div role="tablist" aria-label={t("console.ops.tabs_label")}>
        {TABS.map((x) => (
          <button
            key={x.id}
            type="button"
            role="tab"
            id={`z06-tab-${x.id}`}
            aria-selected={x.id === tab}
            aria-controls="z06-panel"
            onClick={() => {
              setNotice(undefined);
              setTab(x.id);
            }}
          >
            {t(x.label)} <span className="count">{counts.get(x.id)?.open ?? 0}</span>
          </button>
        ))}
      </div>
      <div role="tabpanel" id="z06-panel" aria-labelledby={`z06-tab-${tab}`}>
        <label>
          {t("console.ops.filter_label")}
          <select name="status" value={status} onChange={(e) => setStatus(e.target.value as "open" | "closed")}>
            <option value="open">{t("console.ops.filter_open")}</option>
            <option value="closed">{t("console.ops.filter_closed")}</option>
          </select>
        </label>
        {notice && (
          <p role="status" className="notice">
            {t(notice)}
          </p>
        )}
        {loadError && (
          <p role="alert" className="error">
            {loadError}
          </p>
        )}
        {answer === undefined && !loadError && <p aria-busy="true">{t("console.common.loading")}</p>}
        {current?.awaiting.map((a) => (
          <p key={a} className="awaiting">
            {t(AWAITING[a])}
          </p>
        ))}
        {answer && items.length === 0 && <p className="empty">{status === "open" ? t("console.ops.empty_open") : t("console.ops.empty_closed")}</p>}
        {items.length > 0 && (
          <table>
            <thead>
              <tr>
                <th scope="col">{t("console.ops.column_tenant")}</th>
                <th scope="col">{t("console.ops.column_item")}</th>
                <th scope="col">{t("console.ops.column_detail")}</th>
                <th scope="col">{t("console.ops.column_since")}</th>
                <th scope="col">{t("console.ops.column_assignee")}</th>
                <th scope="col">{t("console.ops.column_actions")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={`${item.sourceType}:${item.sourceId}:${generation}`}>
                  <td>{item.tenant.name}</td>
                  <td>{KIND[item.kind] ? t(KIND[item.kind]!) : item.kind}</td>
                  <td>
                    <dl>
                      {Object.entries(item.detail).map(([k, v]) => (
                        <div key={k}>
                          <dt>{DETAIL[k] ? t(DETAIL[k]!) : k}</dt>
                          <dd>{String(v ?? "")}</dd>
                        </div>
                      ))}
                    </dl>
                  </td>
                  <td>{dateTime.format(new Date(item.occurredAt))}</td>
                  <td>
                    {item.status === "closed" ? (
                      <>
                        <p>
                          {t("console.ops.closed_by")} <strong>{item.closedBy?.name}</strong>
                        </p>
                        {item.closedAt && <p>{dateTime.format(new Date(item.closedAt))}</p>}
                        <p className="note">{item.closeNote}</p>
                      </>
                    ) : item.assignee ? (
                      item.assignee.name
                    ) : (
                      t("console.ops.unassigned")
                    )}
                  </td>
                  <td>
                    <ItemActions
                      item={item}
                      me={me}
                      assignees={assignees}
                      onReload={reload}
                      onChanged={(next, message) => {
                        setNotice(message);
                        setAnswer((prev) =>
                          prev && {
                            ...prev,
                            items:
                              // A closed item leaves the open list, and an open one the closed list.
                              next.status !== prev.status
                                ? prev.items.filter((x) => !(x.sourceType === next.sourceType && x.sourceId === next.sourceId))
                                : prev.items.map((x) => (x.sourceType === next.sourceType && x.sourceId === next.sourceId ? next : x)),
                          },
                        );
                        if (next.status !== status) void load();
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
